<template>
  <v-container class="ma-0 pa-0">
    <v-dialog transition="dialog-bottom-transition" v-model="chooserOpen">
      <template v-slot:activator="{ on, attrs }">
        <label class="pl-0 text-body-1 d-block">
            <v-card v-if="selectedImageElement && large" class="large-activator" :style="largeActivatorStyle" v-bind="attrs" v-on="on">
              <component
                :is="selectedImageElement.component"
                v-if="selectedImageElement.component"
                size="100%"
              ></component>
              <v-img
                v-else
                :src="activatorImageSrc"
                width="100%"
                height="100%"
                :contain="activatorFit === 'contain'"
                :class="{ 'checkerboard-bg': activatorFit === 'contain' }"
              ></v-img>
              <slot name="overlay" :element="selectedImageElement"></slot>
              <v-btn class="large-activator__change" small text color="white">ändern &hellip;</v-btn>
            </v-card>
            <v-card class="d-flex child-flex" v-else-if="selectedImageElement">
              <component
                :is="selectedImageElement.component"
                v-if="selectedImageElement.component"
                size="6em"
              ></component>
              <v-img
                v-else
                :aspect-ratio="1"
                :src="selectedImageElement.thumb512c ? selectedImageElement.thumb512c.download_url : selectedImageElement.thumb512.download_url"
                width="6em"
                height="6em"
                min-width="6em"
                min-height="6em"
                contain
                class="flex-shrink-0 flex-grow-0 checkerboard-bg"
              ></v-img>
              <div class="align-self-center flex-grow-1 pl-2">
                <span class="name-limited-width" v-if="selectedImageElement.image.name">{{ selectedImageElement.image.name }}</span>
                <span class="d-inline-block pl-1 overflow-auto" v-if="selectedImageElement.image.width && selectedImageElement.image.height">({{selectedImageElement.image.width}} x {{selectedImageElement.image.height}})</span>
              </div>
              <v-btn class="align-self-center flex-grow-0 mr-4" text color="primary" v-bind="attrs" v-on="on">ändern &hellip;</v-btn>
            </v-card>
            <v-btn v-else color="primary" v-bind="attrs" v-on="on">{{ subject }} wählen &hellip;</v-btn>
        </label>
      </template>
      <v-card dense>
        <v-toolbar flat dense>
          <v-toolbar-title>{{ subject }} wählen oder eigenes {{ subject }} hochladen</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="chooserOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="mb-0 pb-0">
          <v-sheet class="overflow-y-auto my-0" max-height="calc(95vh - 18em)">
            <v-item-group v-model="selectedImage">
            <v-container>
              <v-row class="align-center">
                <v-col
                  v-for="element in this.selectableImages"
                  :key="element.image.key"
                  cols="10"
                  sm="6"
                  md="4"
                  lg="3"
                  xl="3"
                >
                  <v-item v-slot="{ active, toggle }">
                    <v-card
                      :class="[active ? 'activeborder' : 'inactiveborder']"
                      @click="toggle(); chooserOpen = false"
                      class="pa-1"
                    >
                      <template v-if="element.component">
                        <!-- FIXME Make this a slot instead? -->
                        <component
                          :is="element.component"
                          size="100%"
                        ></component>
                        Kein Logo gesetzt
                      </template>
                      <v-img
                        v-else
                        :src="element.thumb512.download_url"
                        :id="element.image.url"
                        :lazy-src="element.thumb128 ? element.thumb128.download_url : null"
                        :aspect-ratio="element.thumb512.width / element.thumb512.height"
                        contain
                        class="grey lighten-2 checkerboard-bg"
                      >
                      </v-img>
                    </v-card>
                  </v-item>
                </v-col>
              </v-row>
            </v-container>
          </v-item-group>
          </v-sheet>
        </v-card-text>
        <v-card-actions>
          <v-form ref="uploadForm" v-model="uploadForm">
            <v-row>
              <slot name="uploadText"></slot>
              <v-col class="flex-grow-1">
                <v-file-input
                  id="image-selector"
                  v-model="imageUploadFile"
                  :rules="uploadFieldRules"
                  accept="image/png, image/jpeg, image/bmp"
                  type="file"
                  prepend-icon="mdi-camera"
                  show-size
                />
              </v-col>
              <v-col cols="2">
                <v-btn
                  class="ml-2"
                  color="primary"
                  @click="$emit('upload-file', imageUploadFile)"
                  :disabled="!imageUploadFile || !uploadForm"
                >{{ subject }} hochladen</v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { FETCH_CONTENTIMAGES, FETCH_ORGANIZATIONS } from '@/store/action-types'
import { mapGetters, mapState } from 'vuex'

export default {
  name: 'ImageChooser',
  props: {
    value: String,
    subject: {
      type: String,
      default: "Bild",
    },
    initialValue: {
      optional: true,
      validator: prop => typeof prop === 'string' || prop === null,
    },
    imageType: {
      type: [String, Array],
      optional: true,
    },
    featuredValues: {
      type: Array,
      optional: true,
      default: () => [],
    },
    uploadFieldRules: {
      type: Array,
      default: () => [],
    },
    nullComponent: {
      type: Object,
      optional: true,
      default: null,
    },
    nullText: {
      type: String,
      optional: true,
    },
    large: {
      type: Boolean,
      default: false,
    },
    activatorAspectRatio: {
      type: [String, Number],
      default: 2,
    },
    activatorFit: {
      // 'cover' fills the whole box (cropping as needed) - for photographic backgrounds.
      // 'contain' always shows the whole image, capped to activatorHeight - for logos,
      // which must never be cropped; the box then sizes its width to the image itself.
      type: String,
      default: 'cover',
    },
    activatorHeight: {
      type: [String, Number],
      default: 160,
    },
  },
  data () {
    return {
      selectedImage: 0,
      chooserOpen: false,
      imageUploadFile: null,
      uploadForm: false,
    }
  },
  watch: {
    selectedImageInitial: { // FIXME Not correctly updated on upload
      immediate: true,
      handler (newVal) {
        this.selectedImage = newVal
      }
    },
    value (newVal, oldVal) {
      if (this.chooserOpen && newVal !== oldVal) {
        this.chooserOpen = false
      }
    },
    selectedImage (newVal) {
      if (newVal >= 0) {
        const hash = this.selectableImages[newVal].image.hash
        if (hash !== this.value) {
          this.$emit("input", hash)
        }
      }
    },
    chooserOpen: {
      immediate: true,
      handler (newVal) {
        if (!newVal) {
          this.imageUploadFile = null
        }
      }
    },
  },
  async mounted () {
    await Promise.allSettled([
      this.$store.dispatch('contentimage/' + FETCH_CONTENTIMAGES),
      this.$store.dispatch('organization/' + FETCH_ORGANIZATIONS),
    ])
  },
  computed: {
    ...mapState('contentimage', ['images']),
    ...mapGetters("organization", ['currentOrganization']),
    ...mapGetters("organization", { organizationParents: 'parents' }),
    selectedImageInitial () {
      return this.selectableImages.findIndex(element => this.value === element.image.hash)
    },
    selectedImageElement () {
      if (this.selectedImage >= 0 && this.selectedImage < this.selectableImages.length) {
        return this.selectableImages[this.selectedImage]
      }
      return null
    },
    largeActivatorStyle () {
      if (this.activatorFit !== 'contain') {
        return { aspectRatio: String(this.activatorAspectRatio), width: '100%' }
      }
      const height = Number(this.activatorHeight) || 160
      // Use the original image's own dimensions, not a thumbnail's - thumb512c in
      // particular is a square-cropped preview and would force a square box here.
      const image = this.selectedImageElement && !this.selectedImageElement.component
        ? this.selectedImageElement.image
        : null
      const naturalAspect = (image && image.width && image.height) ? image.width / image.height : 1
      return { height: height + 'px', width: Math.round(height * naturalAspect) + 'px' }
    },
    activatorImageSrc () {
      if (!this.selectedImageElement || this.selectedImageElement.component) {
        return null
      }
      // Logos (contain) must never use the pre-cropped square thumbnail - it would
      // show a cropped image regardless of how the box itself is sized.
      if (this.activatorFit === 'contain') {
        return this.selectedImageElement.thumb512.download_url
      }
      return this.selectedImageElement.thumb512c
        ? this.selectedImageElement.thumb512c.download_url
        : this.selectedImageElement.thumb512.download_url
    },
    selectableImages () {
      const retVal = []
      let firstImage = null
      const preferredImages = []

      for (const image of this.images) {
        if (this.imageType) {
          const itCheck = Array.isArray(this.imageType) ? this.imageType : [this.imageType]
          if (!itCheck.some(x => image.types.includes(x))) {
            continue
          }
        }

        if (!image.public) {
          continue
        }

        const nonCroppedThumbs = image.thumbnails.filter(thumb => !thumb.cropped)
        let thumb512 = nonCroppedThumbs.find(thumb => (thumb.width === 512 || thumb.height === 512))
        const thumb128 = nonCroppedThumbs.find(thumb => (thumb.width === 128 || thumb.height === 128))
        const thumb512c = image.thumbnails.find(thumb => (thumb.cropped && thumb.width === 512))

        if (!thumb512) {
          if (thumb128) {
            thumb512 = thumb128
          } else {
            thumb512 = image.thumbnails[0] ?? null
          }
        }

        if (!thumb512) {
          continue
        }

        const element = {
          image,
          thumb128,
          thumb512,
          thumb512c,
        }

        if (image.hash === this.initialValue) {
          firstImage = element
        } else {
          if (image.organization) {
            if (image.organization === this.currentOrganization.url) {
              preferredImages.push(element)
            } else if ((this.organizationParents[this.currentOrganization.url] ?? []).includes(image.organization)) {
              retVal.push(element)
            }
          } else {
            if (this.featuredValues && this.featuredValues.includes(image.hash)) {
              retVal.unshift(element)
            } else {
              retVal.push(element)
            }
          }
        }
      }

      if (firstImage && !preferredImages.concat(retVal).some(el => el.image.hash === firstImage.image.hash)) {
        preferredImages.unshift(firstImage)
      }
      const nullEntry = this.nullComponent
        ? [{
            image: {
              hash: null,
              key: "nullComponent",
              name: this.nullText
                ? this.nullText
                : `Kein ${this.subject} eingestellt`
            },
            component: this.nullComponent,
          }]
        : []
      return [...nullEntry, ...preferredImages, ...retVal]
    },

  }

}
</script>

<style scoped lang="stylus">
.activeborder
  border: 4px solid $clr-primary!important
.inactiveborder
  border: 4px solid transparent!important
.name-limited-width
  display: inline-block
  white-space: nowrap
  max-width: 15em
  overflow-x: hidden
  text-overflow: ellipsis
.large-activator
  position: relative
  overflow: hidden
  cursor: pointer
  max-width: 100%
  flex-shrink: 0
.large-activator__change
  position: absolute
  right: 8px
  bottom: 8px
  background: rgba(0, 0, 0, .55) !important
</style>
